/* eslint no-underscore-dangle: 0 */
import React, { useEffect, useMemo, useState } from 'react';
import _debounce from 'lodash.debounce';

// material-ui
import {
    Alert,
    Autocomplete,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    FormGroup,
    Grid,
    Link,
    MenuItem,
    Radio,
    RadioGroup,
    TextField,
    Typography
} from '@mui/material';

// third-party
import dayjs from 'dayjs';
import { Link as RouterLink } from 'react-router-dom';

// project imports
import StaffSelect from 'components/crm/StaffSelect';
import useCrmGQL from 'hooks/crm/useGQL';
import { CrmAccount, CrmContact, CrmTask, CrmTaskScopeOption, RecurrencePatternInput } from 'types/crm';
import {
    defaultRecurrenceUiState,
    describeRecurrence,
    FREQUENCY_OPTIONS,
    fromRecurrencePattern,
    isRecurringUi,
    RecurrenceEndMode,
    RecurrenceUiFrequency,
    RecurrenceUiState,
    toRecurrencePatternInput,
    WEEKDAYS
} from './recurrencePattern';

export type TaskDialogValues = {
    title: string;
    description?: string;
    ownerId?: string;
    /** Existing CRM account to link; null clears (edit only); undefined leaves unset. */
    accountId?: string | null;
    /** Existing CRM contact to link; null clears (edit only); undefined leaves unset. */
    contactId?: string | null;
    /** ISO datetime, or null to clear (edit only). */
    dueAt?: string | null;
    reminderAt?: string | null;
    /** Recurrence toggle (POGO-13345). */
    isRecurring?: boolean;
    /** The rule when recurring; null clears it (edit only); undefined leaves unset. */
    recurrencePattern?: RecurrencePatternInput | null;
    /** Edit scope, chosen when saving an already-recurring task (POGO-13345). */
    scope?: CrmTaskScopeOption;
};

type TaskDialogProps = {
    open: boolean;
    /** null = creating */
    task: CrmTask | null;
    admins: { _id: string; name: string }[];
    saving: boolean;
    onClose: () => void;
    onSave: (values: TaskDialogValues) => void;
};

/** A picked link — the id kept for the mutation plus a label for display. */
type LinkOption = { id: string; label: string };

type FormState = {
    title: string;
    description: string;
    ownerId: string;
    /** datetime-local values (browser-local wall time); '' = unset */
    dueAt: string;
    reminderAt: string;
};

const toLocalInput = (iso?: string | null) => (iso ? dayjs(iso).format('YYYY-MM-DDTHH:mm') : '');

const contactLabel = (contact: CrmContact) =>
    [[contact.firstName, contact.lastName].filter(Boolean).join(' '), contact.businessName].filter(Boolean).join(' · ');

/**
 * Create/edit dialog for a CRM task (POGO-12603 Phase 8). Owner defaults to
 * the viewer ("Me") on create; due/reminder times are picked as local wall
 * times in the browser timezone and sent as ISO datetimes.
 *
 * Wave 3 Phase 10 adds searchable business + contact pickers so a task can be
 * attached to a specific account and/or person; picking a business scopes the
 * contact search to that account.
 */
const TaskDialog = ({ open, task, admins, saving, onClose, onSave }: TaskDialogProps) => {
    const { LIST_ACCOUNTS_LAZY, LIST_ALL_CONTACTS_LAZY } = useCrmGQL();

    const [form, setForm] = useState<FormState>({ title: '', description: '', ownerId: '', dueAt: '', reminderAt: '' });
    const [account, setAccount] = useState<LinkOption | null>(null);
    const [contact, setContact] = useState<LinkOption | null>(null);
    const [recurrence, setRecurrence] = useState<RecurrenceUiState>(defaultRecurrenceUiState());
    // Scope prompt shown when saving an edit to an already-recurring task.
    const [scopeOpen, setScopeOpen] = useState(false);
    const [scope, setScope] = useState<CrmTaskScopeOption>('thisAndFuture');
    const [pendingValues, setPendingValues] = useState<TaskDialogValues | null>(null);

    const [searchAccounts, { data: accountData, loading: accountsLoading }] = LIST_ACCOUNTS_LAZY();
    const [searchContacts, { data: contactData, loading: contactsLoading }] = LIST_ALL_CONTACTS_LAZY();

    useEffect(() => {
        if (open) {
            setForm({
                title: task?.title || '',
                description: task?.description || '',
                ownerId: task?.ownerId || '',
                dueAt: toLocalInput(task?.dueAt),
                reminderAt: toLocalInput(task?.reminderAt)
            });
            setAccount(task?.accountId ? { id: task.accountId, label: task.accountName || 'Linked business' } : null);
            setContact(task?.contactId ? { id: task.contactId, label: task.contactName || 'Linked contact' } : null);
            setRecurrence(task?.isRecurring ? fromRecurrencePattern(task.recurrencePattern) : defaultRecurrenceUiState());
            setScopeOpen(false);
            setScope('thisAndFuture');
            setPendingValues(null);
        }
    }, [open, task]);

    const runAccountSearch = useMemo(
        () =>
            _debounce((text: string) => {
                searchAccounts({
                    variables: { input: { pageMeta: { skip: 0, limit: 20 }, filter: text ? { searchText: text } : undefined } }
                });
            }, 300),
        [searchAccounts]
    );

    const runContactSearch = useMemo(
        () =>
            _debounce((text: string, accountId?: string) => {
                searchContacts({
                    variables: {
                        input: {
                            pageMeta: { skip: 0, limit: 20 },
                            filter: {
                                ...(text ? { searchText: text } : {}),
                                ...(accountId ? { accountId } : {})
                            }
                        }
                    }
                });
            }, 300),
        [searchContacts]
    );

    useEffect(
        () => () => {
            runAccountSearch.cancel();
            runContactSearch.cancel();
        },
        [runAccountSearch, runContactSearch]
    );

    const accountOptions: LinkOption[] = useMemo(
        () =>
            (accountData?.listCrmAccounts?.data || [])
                .filter((a: CrmAccount) => a.businessName)
                .map((a: CrmAccount) => ({ id: a._id, label: a.businessName })),
        [accountData]
    );

    const contactOptions: LinkOption[] = useMemo(
        () => (contactData?.listAllCrmContacts?.data || []).map((c: CrmContact) => ({ id: c._id, label: contactLabel(c) })),
        [contactData]
    );

    const buildValues = (): TaskDialogValues => {
        const editing = !!task;
        const toIso = (local: string) => {
            if (local) {
                return new Date(local).toISOString();
            }
            // on edit an emptied picker clears the date; on create it's just unset
            return editing ? null : undefined;
        };
        // on edit, an unset link is sent as null to clear; on create it's undefined
        const linkValue = (picked: LinkOption | null) => (picked ? picked.id : editing ? null : undefined);

        const dueAt = toIso(form.dueAt);
        const recurring = isRecurringUi(recurrence);
        const pattern = toRecurrencePatternInput(recurrence, dueAt);

        return {
            title: form.title.trim(),
            description: form.description.trim() || undefined,
            ownerId: form.ownerId || undefined,
            accountId: linkValue(account),
            contactId: linkValue(contact),
            dueAt,
            reminderAt: toIso(form.reminderAt),
            isRecurring: recurring,
            // recurring: the rule; not recurring: clear on edit, leave unset on create
            recurrencePattern: recurring ? pattern ?? undefined : editing ? null : undefined
        };
    };

    const handleSave = () => {
        const values = buildValues();
        // Editing an already-recurring task offers the calendar-style scope
        // choice; a new task (or turning recurrence on for the first time) just
        // saves — there's no earlier occurrence to distinguish from.
        if (task?.isRecurring) {
            setPendingValues(values);
            setScopeOpen(true);
            return;
        }
        onSave(values);
    };

    const confirmScope = () => {
        if (pendingValues) {
            onSave({ ...pendingValues, scope });
        }
    };

    const recurring = isRecurringUi(recurrence);
    // a recurring task needs a due date to anchor the series
    const missingRecurrenceAnchor = recurring && !form.dueAt;
    const recurrenceSummary = describeRecurrence(recurrence, form.dueAt ? new Date(form.dueAt).toISOString() : null);

    const toggleWeekday = (day: number) =>
        setRecurrence((prev) => ({
            ...prev,
            daysOfWeek: prev.daysOfWeek.includes(day) ? prev.daysOfWeek.filter((d) => d !== day) : [...prev.daysOfWeek, day]
        }));

    const showWeekdays = recurrence.frequency === 'weekly' || recurrence.frequency === 'fortnightly';
    const showInterval = recurrence.frequency !== 'none' && recurrence.frequency !== 'fortnightly';
    const intervalUnit = { daily: 'day(s)', weekly: 'week(s)', monthlyDate: 'month(s)', monthlyNth: 'month(s)', annually: 'year(s)' }[
        recurrence.frequency as Exclude<RecurrenceUiFrequency, 'none' | 'fortnightly'>
    ];

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{task ? 'Edit task' : 'New task'}</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 0 }}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Title"
                            value={form.title}
                            onChange={(e) => setForm({ ...form, title: e.target.value })}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            size="small"
                            multiline
                            minRows={3}
                            label="Notes"
                            value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                        />
                    </Grid>
                    {/* Read-only links to the linked business/contact's CRM detail page (POGO-13786). */}
                    {(task?.accountId || task?.contactId) && (
                        <Grid item xs={12}>
                            <Typography variant="caption" color="textSecondary" display="block">
                                Linked
                            </Typography>
                            {task?.accountId && (
                                <Link component={RouterLink} to={`/crm/accounts/${task.accountId}`} onClick={onClose} display="block">
                                    {task.accountName || 'Linked business'}
                                </Link>
                            )}
                            {task?.contactId && (
                                <Link component={RouterLink} to={`/crm/contacts/${task.contactId}`} onClick={onClose} display="block">
                                    {task.contactName || 'Linked contact'}
                                </Link>
                            )}
                        </Grid>
                    )}
                    <Grid item xs={12} sm={6}>
                        <Autocomplete<LinkOption>
                            size="small"
                            fullWidth
                            options={accountOptions}
                            loading={accountsLoading}
                            value={account}
                            isOptionEqualToValue={(option, selected) => option.id === selected.id}
                            onOpen={() => runAccountSearch('')}
                            onInputChange={(_event, next, reason) => {
                                if (reason === 'input') {
                                    runAccountSearch(next.trim());
                                }
                            }}
                            onChange={(_event, next) => {
                                setAccount(next);
                                // re-scope the contact search to the newly picked business
                                if (next) {
                                    runContactSearch('', next.id);
                                    // drop a contact that belonged to a different business
                                    setContact(null);
                                }
                            }}
                            renderInput={(params) => (
                                <TextField
                                    // eslint-disable-next-line react/jsx-props-no-spreading
                                    {...params}
                                    label="Business (optional)"
                                    placeholder="Search a business"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {accountsLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        )
                                    }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Autocomplete<LinkOption>
                            size="small"
                            fullWidth
                            options={contactOptions}
                            loading={contactsLoading}
                            value={contact}
                            isOptionEqualToValue={(option, selected) => option.id === selected.id}
                            onOpen={() => runContactSearch('', account?.id)}
                            onInputChange={(_event, next, reason) => {
                                if (reason === 'input') {
                                    runContactSearch(next.trim(), account?.id);
                                }
                            }}
                            onChange={(_event, next) => setContact(next)}
                            renderInput={(params) => (
                                <TextField
                                    // eslint-disable-next-line react/jsx-props-no-spreading
                                    {...params}
                                    label="Contact (optional)"
                                    placeholder="Search a contact"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {contactsLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        )
                                    }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <StaffSelect
                            label="Owner"
                            // "Me" (→ empty owner) only offered when creating, as before.
                            sentinelLabel={!task ? 'Me' : undefined}
                            staff={admins}
                            value={form.ownerId}
                            onChange={(id) => setForm({ ...form, ownerId: id })}
                            helperText={!task && !form.ownerId ? 'Defaults to you' : undefined}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            size="small"
                            type="datetime-local"
                            label="Due"
                            value={form.dueAt}
                            onChange={(e) => setForm({ ...form, dueAt: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            size="small"
                            type="datetime-local"
                            label="Reminder"
                            value={form.reminderAt}
                            onChange={(e) => setForm({ ...form, reminderAt: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                            helperText="Emails the owner with the daily CRM sweep"
                        />
                    </Grid>

                    {/* Repeat (POGO-13345) — reuses the calendar picker idiom, bound to RecurrencePatternInput */}
                    <Grid item xs={12}>
                        <Divider textAlign="left" sx={{ mt: 1 }}>
                            <Typography variant="caption" color="secondary" sx={{ letterSpacing: '0.06em' }}>
                                REPEAT
                            </Typography>
                        </Divider>
                    </Grid>
                    <Grid item xs={12} sm={showInterval ? 6 : 12}>
                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="Frequency"
                            value={recurrence.frequency}
                            onChange={(e) => setRecurrence({ ...recurrence, frequency: e.target.value as RecurrenceUiFrequency })}
                        >
                            {FREQUENCY_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    {showInterval && (
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label={`Every (${intervalUnit})`}
                                value={recurrence.interval}
                                onChange={(e) => setRecurrence({ ...recurrence, interval: Math.max(1, Number(e.target.value) || 1) })}
                                inputProps={{ min: 1 }}
                            />
                        </Grid>
                    )}
                    {showWeekdays && (
                        <Grid item xs={12}>
                            <FormGroup row>
                                {WEEKDAYS.map((w) => (
                                    <FormControlLabel
                                        key={w.day}
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={recurrence.daysOfWeek.includes(w.day)}
                                                onChange={() => toggleWeekday(w.day)}
                                            />
                                        }
                                        label={w.label}
                                    />
                                ))}
                            </FormGroup>
                        </Grid>
                    )}
                    {recurring && (
                        <>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label="Ends"
                                    value={recurrence.endMode}
                                    onChange={(e) => setRecurrence({ ...recurrence, endMode: e.target.value as RecurrenceEndMode })}
                                >
                                    <MenuItem value="never">Never</MenuItem>
                                    <MenuItem value="count">After a number of times</MenuItem>
                                    <MenuItem value="date">On a date</MenuItem>
                                </TextField>
                            </Grid>
                            {recurrence.endMode === 'count' && (
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        type="number"
                                        label="Occurrences"
                                        value={recurrence.endAfterOccurrence}
                                        onChange={(e) =>
                                            setRecurrence({ ...recurrence, endAfterOccurrence: Math.max(1, Number(e.target.value) || 1) })
                                        }
                                        inputProps={{ min: 1, max: 365 }}
                                    />
                                </Grid>
                            )}
                            {recurrence.endMode === 'date' && (
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        type="date"
                                        label="End date"
                                        value={recurrence.endDate ? dayjs(recurrence.endDate).format('YYYY-MM-DD') : ''}
                                        onChange={(e) =>
                                            setRecurrence({
                                                ...recurrence,
                                                endDate: e.target.value ? new Date(e.target.value).toISOString() : null
                                            })
                                        }
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                            )}
                            <Grid item xs={12}>
                                <Alert severity={missingRecurrenceAnchor ? 'warning' : 'info'}>{recurrenceSummary}</Alert>
                            </Grid>
                        </>
                    )}
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" disabled={saving || !form.title.trim() || missingRecurrenceAnchor} onClick={handleSave}>
                    {saving ? 'Saving...' : task ? 'Save' : 'Create task'}
                </Button>
            </DialogActions>

            {/* Recurring edit scope prompt (POGO-13345) — the calendar's idiom. */}
            <Dialog open={scopeOpen} onClose={() => setScopeOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Edit recurring task</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        This is part of a recurring series. What would you like to change?
                    </Typography>
                    <RadioGroup value={scope} onChange={(e) => setScope(e.target.value as CrmTaskScopeOption)}>
                        <FormControlLabel value="thisOnly" control={<Radio />} label="This occurrence only" />
                        <FormControlLabel value="thisAndFuture" control={<Radio />} label="This and future occurrences" />
                    </RadioGroup>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setScopeOpen(false)}>Back</Button>
                    <Button variant="contained" disabled={saving} onClick={confirmScope}>
                        {saving ? 'Saving...' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
};

export default TaskDialog;
